import { create } from "zustand";
import type { UserVo } from "@zhihaoscm/entity";

export const USER_STORE_KEY = "zhihao-user";

export type ReactPermissionRoute = {
    name?: string;
    path?: string;
    children?: ReactPermissionRoute[];
    meta?: Record<string, unknown>;
};

const emptyUserInfo = (): UserVo =>
    ({
        roles: [],
        permissions: [],
        user: {}
    }) as unknown as UserVo;

/** 读取旧 Pinia 持久化结构，确保 Zustand 接管时不丢失已登录会话。 */
const getInitialUserState = () => {
    if (typeof localStorage === "undefined") {
        return { token: "", userInfo: emptyUserInfo() };
    }

    try {
        const persisted = JSON.parse(localStorage.getItem(USER_STORE_KEY) || "{}") as {
            token?: string;
            userInfo?: UserVo;
        };
        return {
            token: persisted.token || "",
            userInfo: persisted.userInfo || emptyUserInfo()
        };
    } catch {
        return { token: "", userInfo: emptyUserInfo() };
    }
};

/** 仅持久化后端会话所需字段，避免把 Zustand action 序列化进旧存储键。 */
const persistUserState = (token: string, userInfo: UserVo) => {
    if (typeof localStorage !== "undefined") {
        localStorage.setItem(USER_STORE_KEY, JSON.stringify({ token, userInfo }));
    }
};

export interface ReactUserStore {
    token: string;
    userInfo: UserVo;
    banButton: boolean;
    menuList: ReactPermissionRoute[];
    flatMenuList: ReactPermissionRoute[];
    setToken: (token: string) => void;
    setUserInfo: (userInfo: UserVo) => void;
    setMenuList: (menuList: ReactPermissionRoute[]) => void;
    setFlatMenuList: (flatMenuList: ReactPermissionRoute[]) => void;
    hasPermission: (permission: string) => boolean;
    hasAnyPermission: (permissions: string[]) => boolean;
}

const initialUserState = getInitialUserState();

/** 用户会话 Store，保留 Vue 版本 token/userInfo/menu 的业务边界。 */
export const useUserStore = create<ReactUserStore>((set, get) => ({
    token: initialUserState.token,
    userInfo: initialUserState.userInfo,
    banButton: initialUserState.userInfo.permissions?.includes("ROLE_BUTTON_BAN") ?? false,
    menuList: [],
    flatMenuList: [],
    setToken: token => {
        set({ token });
        persistUserState(token, get().userInfo);
    },
    setUserInfo: userInfo => {
        set({ userInfo, banButton: userInfo.permissions?.includes("ROLE_BUTTON_BAN") ?? false });
        persistUserState(get().token, userInfo);
    },
    setMenuList: menuList => set({ menuList }),
    setFlatMenuList: flatMenuList => set({ flatMenuList }),
    hasPermission: permission => Boolean(get().userInfo.permissions?.includes(permission)),
    hasAnyPermission: permissions => permissions.some(permission => get().hasPermission(permission))
}));

export interface ReactAuthStore {
    authButtonList: Record<string, string[]>;
    authMenuList: ReactPermissionRoute[];
    routeName: string;
    setAuthButtonList: (authButtonList: Record<string, string[]>) => void;
    setAuthMenuList: (authMenuList: ReactPermissionRoute[]) => void;
    setRouteName: (routeName: string) => void;
    can: (permission: string, routeName?: string) => boolean;
}

/** 路由和按钮权限 Store，替代 Vue auth store 的 getters/actions。 */
export const useAuthStore = create<ReactAuthStore>((set, get) => ({
    authButtonList: {},
    authMenuList: [],
    routeName: "",
    setAuthButtonList: authButtonList => set({ authButtonList }),
    setAuthMenuList: authMenuList => set({ authMenuList }),
    setRouteName: routeName => set({ routeName }),
    can: (permission, routeName = get().routeName) => Boolean(get().authButtonList[routeName]?.includes(permission))
}));
